import { DualCoordinateDepthRange, LengthIndicator } from '@da-g3-acl/shared/common';
import { HEX } from '@da-g3-acl/shared/common/colour-types';

import { RulerSegment } from '../../../helpers/image-to-ruler-allocation';
import { LaneThickness } from '../../../types/imageTypeMetadata';
import { LengthRange } from '../../../types/lengthRange';
import { RangeHighlight } from '../../../types/rangeHighlight';

import { ColourProvider, NumericLaneRowConfiguration } from './NumericPointsLaneRow/configuration';
import { Bounds } from './NumericPointsLaneRow/useGradients';

export enum CoreLaneRowType {
	Image,
	Interval,
	Ruler,
	Numeric,
}
export enum CoreLaneRowAlignment {
	Top,
	Centre,
	Bottom,
}

export type CoreViewerConfiguration = CorePanelConfiguration[];
export type CorePanelConfiguration = CoreLaneRowConfiguration[];

export type CoreLaneRowConfiguration =
	| ImageLaneRowConfiguration
	| IntervalLaneRowConfiguration
	| RulerLaneRowConfiguration
	| NumericLaneRowConfiguration;

export interface LaneRowConfiguration {
	id: string;
	type: CoreLaneRowType;
	alignment?: CoreLaneRowAlignment;
	isLoading: boolean;
}

export type ImageLaneRowHoverCallbackFn = (at?: number, colour?: string) => void;

export type IntervalLaneRowMouseEventCallbackFn = (
	at: number | undefined,
	interval: IntervalLaneRowInterval | undefined,
	renderedIntervalColour: HEX[] | undefined,
) => void;
export type IndicatorLaneRowHoverCallbackFn = (indicator?: LengthIndicator) => void;
export type LaneRowClickCallbackFn = (at: number) => void;
export enum IntervalResizeHandle {
	Start = 'start',
	End = 'end',
}
export type IntervalHandleResizeHandleMouseDownFn = (
	configuration: string,
	interval: IntervalLaneRowInterval,
	handle: IntervalResizeHandle,
) => void;

export type IntervalDrawMouseDownFn = (configuration: string, clickDepth: number, ctrlKey: boolean) => void;

export type ImageLaneRowConfiguration = LaneRowConfiguration & {
	type: CoreLaneRowType.Image;
	imageTypeId: number;
	draw?: RangeHighlight[];
	calculateColourOnHover?: boolean;
	onHover?: ImageLaneRowHoverCallbackFn;
	onClick?: LaneRowClickCallbackFn;
	isUncropped: boolean;
};

export type IntervalLaneRowConfiguration = LaneRowConfiguration & {
	type: CoreLaneRowType.Interval;
	intervalSetId: string;
	thickness?: LaneThickness;
	valueBounds: Bounds;
	onHover?: IntervalLaneRowMouseEventCallbackFn;
	onSingleClick?: IntervalLaneRowMouseEventCallbackFn;
	onDoubleClick?: IntervalLaneRowMouseEventCallbackFn;
	canEdit?: boolean;
	canDraw?: boolean;
	allowOverlap?: boolean;
	maxOverlapCount?: number;
	colourProvider?: (bounds: Bounds) => ColourProvider;
	editableBounds?: LengthRange[];
	endMarkerColour?: EndMarkerColourProvider;
};

export type EndMarkerColourProvider = (intervalColour: HEX) => HEX;

export type HandleSegmentHoverFn = (segment: RulerSegment | undefined) => void;
export type HandleIndicatorHoverFn = (indicator: LengthIndicator | undefined) => void;

export type RulerLaneRowConfiguration = LaneRowConfiguration & {
	type: CoreLaneRowType.Ruler;
	thickness?: LaneThickness;
	onIndicatorHover?: HandleIndicatorHoverFn | undefined;
	onSegmentHover?: HandleSegmentHoverFn | undefined;
	indicators?: LengthIndicator[];
	segments?: RulerSegment[];
};

/**
 * represents an individual value rendered within a parent interval.
 * Given that a single interval might contain multiple values that should be rendered in unison,
 * this encapsulates the visual properties and the backing value that is contained
 */
export interface IntervalLaneRowIntervalValue {
	colour?: HEX;
	value?: number;
	label?: string;
}

/**
 * Represents an interval that should be rendered within a {@link IntervalLaneRow}.
 * Carries depth in both ruler (downhole) and renderable (image/tray) coordinate systems;
 * geometry is painted against `renderableDepth`, while `rulerDepth` is what consumers
 * persist or compare against canonical downhole metres.
 */
export interface IntervalLaneRowInterval extends DualCoordinateDepthRange {
	values?: IntervalLaneRowIntervalValue[];
	selected?: boolean;
}
